import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { X, Globe, Heart, Flame, Gift, ArrowRight } from "lucide-react";

export default function WinbackBanner() {
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkWinback();
  }, []);

  const checkWinback = async () => {
    try {
      const res = await axios.get(`${API}/renewal/winback`);
      if (res.data.campaign) {
        setCampaign(res.data.campaign);
      }
    } catch (error) {
      // Silently fail
    } finally {
      setLoading(false);
    }
  };

  const handleCTAClick = async () => {
    try {
      await axios.post(`${API}/renewal/winback-convert`);
    } catch (error) {
      // Continue anyway
    }
    navigate("/pricing?source=winback");
  };

  if (loading || !campaign || dismissed) {
    return null;
  }

  const primary = campaign.primary || {};
  const offer = campaign.offer || {};

  // Choose icon based on personalization
  const getIcon = () => {
    if (campaign.personalization?.includes("world_map")) return Globe;
    if (campaign.personalization?.includes("partner_activity")) return Heart;
    if (campaign.personalization?.includes("streak")) return Flame;
    return Gift;
  };

  const Icon = getIcon();

  return (
    <div className="bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 border-b border-primary/20">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Icon className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-sm truncate">{primary.headline}</div>
              <div className="text-xs text-muted-foreground truncate">{primary.message}</div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0">
            {offer.discount_percent > 0 && (
              <span className="hidden sm:inline-block text-xs font-bold text-green-600 bg-green-500/10 px-2 py-1 rounded-full">
                {offer.discount_percent}% OFF
              </span>
            )}
            <Button 
              size="sm" 
              onClick={handleCTAClick}
              className="gap-1"
            >
              {primary.cta || "Resume"}
              <ArrowRight className="w-4 h-4" />
            </Button>
            <button
              onClick={() => setDismissed(true)}
              className="p-1 hover:bg-muted rounded-full transition-colors"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Partner Activity Highlight */}
        {campaign.partner_activity && (
          <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
            <Heart className="w-3 h-3 text-pink-500" />
            Your partner added {campaign.partner_activity.new_items} new items while you were away
          </div>
        )}
      </div>
    </div>
  );
}
